import { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronRight, ChevronLeft, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function OnboardingTour({ steps, onComplete }) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [rect, setRect] = useState(null);
  const [visible, setVisible] = useState(false);
  const tooltipRef = useRef(null);

  const step = steps[current];
  const isLast = current === steps.length - 1;

  const measureTarget = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(step.target);
    if (el) {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    } else {
      setRect(null);
    }
  }, [step]);

  useEffect(() => {
    // Slight delay for mount animations
    const t = setTimeout(() => setVisible(true), 300);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    measureTarget();
    window.addEventListener('resize', measureTarget);
    window.addEventListener('scroll', measureTarget, true);
    return () => {
      window.removeEventListener('resize', measureTarget);
      window.removeEventListener('scroll', measureTarget, true);
    };
  }, [measureTarget, current]);

  const next = () => {
    if (isLast) { onComplete(dontShowAgain); return; }
    setCurrent(c => c + 1);
  };
  const prev = () => setCurrent(c => Math.max(0, c - 1));
  const skip = () => onComplete(dontShowAgain);

  if (!visible || !step) return null;

  // Calculate tooltip position
  const pad = 12;
  const tooltipStyle = {};
  if (rect) {
    const pos = step.position || 'bottom';
    const tooltipH = 200; // approximate tooltip height
    if (pos === 'bottom') {
      tooltipStyle.top = rect.top + rect.height + pad;
      tooltipStyle.left = Math.max(16, Math.min(rect.left, window.innerWidth - 380));
    } else if (pos === 'top') {
      tooltipStyle.bottom = window.innerHeight - rect.top + pad;
      tooltipStyle.left = Math.max(16, Math.min(rect.left, window.innerWidth - 380));
    } else if (pos === 'right') {
      tooltipStyle.top = Math.max(16, Math.min(rect.top, window.innerHeight - tooltipH - 16));
      tooltipStyle.left = rect.left + rect.width + pad;
    } else if (pos === 'left') {
      tooltipStyle.top = Math.max(16, Math.min(rect.top - tooltipH + rect.height, window.innerHeight - tooltipH - 16));
      tooltipStyle.right = window.innerWidth - rect.left + pad;
    }
  } else {
    // Fallback: center of screen
    tooltipStyle.top = '50%';
    tooltipStyle.left = '50%';
    tooltipStyle.transform = 'translate(-50%, -50%)';
  }

  return (
    <div data-testid="onboarding-tour" className="fixed inset-0 z-[9999]">
      {/* Dark overlay with spotlight cutout */}
      <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'auto' }}>
        <defs>
          <mask id="tour-mask">
            <rect width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={rect.left - 6} y={rect.top - 6}
                width={rect.width + 12} height={rect.height + 12}
                rx="8" fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          width="100%" height="100%"
          fill="rgba(0,0,0,0.7)"
          mask="url(#tour-mask)"
          onClick={skip}
        />
      </svg>

      {/* Spotlight border glow */}
      {rect && (
        <div
          className="absolute rounded-lg border-2 border-sky-400/60 shadow-[0_0_20px_rgba(56,189,248,0.2)] pointer-events-none"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            transition: 'all 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        />
      )}

      {/* Tooltip Card */}
      <div
        ref={tooltipRef}
        data-testid="tour-tooltip"
        className="absolute z-10 w-[340px] rounded-xl shadow-2xl shadow-black/40 border border-sky-500/20 animate-tour-in"
        style={{
          ...tooltipStyle,
          background: 'hsl(222 47% 10% / 0.98)',
          backdropFilter: 'blur(20px)',
        }}
      >
        {/* Header */}
        <div className="px-5 pt-4 pb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-sky-400" />
            <span className="text-[10px] text-sky-400 uppercase tracking-widest font-semibold" style={{ fontFamily: 'Barlow Condensed' }}>
              {t('tour.step')} {current + 1} {t('tour.of')} {steps.length}
            </span>
          </div>
          <button
            data-testid="tour-skip"
            onClick={skip}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {t('tour.skipTour')}
          </button>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={dontShowAgain} onChange={e => setDontShowAgain(e.target.checked)} className="rounded border-border w-3.5 h-3.5" data-testid="tour-dont-show" />
            <span className="text-[10px] text-muted-foreground">{t('tour.dontShowAgain') || "Don't show again"}</span>
          </label>
        </div>

        {/* Content */}
        <div className="px-5 pb-2">
          <h4 className="text-sm font-semibold text-foreground mb-1.5">{step.title}</h4>
          <p className="text-xs text-muted-foreground leading-relaxed">{step.description}</p>
        </div>

        {/* Progress dots + Navigation */}
        <div className="px-5 pb-4 pt-3 flex items-center justify-between border-t border-white/5 mt-2">
          <div className="flex items-center gap-1.5">
            {steps.map((step, i) => (
              <div
                key={step.title || `tour-step-${i}`}
                className={`w-1.5 h-1.5 rounded-full transition-all ${
                  i === current ? 'bg-sky-400 w-4' : i < current ? 'bg-sky-400/40' : 'bg-white/10'
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {current > 0 && (
              <button
                data-testid="tour-prev"
                onClick={prev}
                className="flex items-center gap-1 px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-white/5"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> {t('tour.previous')}
              </button>
            )}
            <button
              data-testid="tour-next"
              onClick={next}
              className="flex items-center gap-1 px-4 py-1.5 bg-sky-500 text-white text-xs font-semibold rounded-md hover:bg-sky-400 transition-colors"
            >
              {isLast ? t('tour.finish') : t('tour.next')} {!isLast && <ChevronRight className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes tourIn {
          from { opacity: 0; transform: translateY(8px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-tour-in { animation: tourIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
      `}</style>
    </div>
  );
}
